import type { PropsWithChildren, FunctionComponent } from 'react'
import { useEffect, useMemo, useState, useCallback } from 'react'
import { type Styles } from 'react-select'
import { SelectField, type Option } from '../../tds'
import { useMessage } from '../../utils/intl/intl.hooks'
import { useLanguageLabels } from '../../utils/intl/useLanguageLabels.hooks'
import { IPP_SUPPORTED_LOCALES } from '../../utils/intl/intl.types'

interface LocaleOption extends Option {
  localeCode: string
  localeId: string
}

export type OnLocaleChangeCallback = (locale: LocaleOption) => void

export interface LocaleSelectProps {
  isDisabled?: boolean
  onChange: OnLocaleChangeCallback
  // Use initialValue for uncontrolled behavior
  initialValue?: string
  // Use value for controlled behavior
  value?: string
  isModernTheme?: boolean
  locales?: Omit<LocaleOption, 'label'>[] | Omit<LocaleOption, 'label' | 'value'>[]
  styles?: Styles
  usePreLoginStyles?: boolean
  defaultLocale?: string
  compact?: boolean
}

const IPP_LOCALES = IPP_SUPPORTED_LOCALES.filter(value => value && value.includes('-')).map(
  locale => ({
    localeCode: locale.replace('-', '_'),
    localeId: locale.replace('-', '_'),
  })
)

const iconMapping = {
  US: '🇺🇸',
  CA: '🇨🇦',
  AU: '🇦🇺',
  UK: '🇬🇧',
  ES: '🇪🇸',
  FR: '🇫🇷',
  default: '',
}

export const LocaleSelect: FunctionComponent<PropsWithChildren<LocaleSelectProps>> = ({
  locales = IPP_LOCALES,
  usePreLoginStyles,
  defaultLocale,
  compact = true,
  ...props
}) => {
  const placeholder = useMessage('components.localeSelect.noLocale')
  const [value, setValue] = useState<LocaleOption | null>(null)

  const languageLabels = useLanguageLabels()

  const localesOptions: LocaleOption[] = useMemo(
    () =>
      locales.map(locale => {
        const [languageCode, countryCode] = locale.localeCode.toUpperCase().split('_')

        const label = [iconMapping[countryCode as keyof typeof iconMapping] || iconMapping.default]

        // Try to get language label using the enhanced getByLocaleCode method
        const languageLabel = languageLabels.getByLocaleCode(locale.localeCode)

        if (languageLabel) {
          label.push(languageLabel)
        } else {
          // If no language label found, check the original message keys directly
          const directLanguageLabel = languageLabels[languageCode as keyof typeof languageLabels]
          if (directLanguageLabel) {
            label.push(directLanguageLabel as string)
          } else {
            // if for some reason the languageCode is not recognized, print the localeCode.
            label.push(locale.localeCode.toUpperCase())
          }
        }

        return { label: label.join(' '), value: locale.localeId, ...locale }
      }),
    [locales, languageLabels]
  )

  const findLocaleByCodeOrId = useCallback(
    (value: string) =>
      localesOptions.find(locale => [locale.localeCode, locale.localeId].includes(value)),
    [localesOptions]
  )
  /**
   * Initial value setting, will try to match either the localeCode or the localeId.
   * This is useful because we don't know which kind of data the consumer will be
   * using.
   */
  useEffect(
    () => {
      if (!props.initialValue) return

      const found = findLocaleByCodeOrId(props.initialValue)

      setValue(found || null)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  useEffect(() => {
    if (props.initialValue) return

    const found = findLocaleByCodeOrId(props.value || '')

    setValue(found || null)
  }, [setValue, findLocaleByCodeOrId, props.value, props.initialValue])

  const onChange: LocaleSelectProps['onChange'] = option => {
    setValue(option)

    if (props.onChange) props.onChange(option)
  }

  return (
    <SelectField
      {...props}
      placeholder={placeholder}
      value={value?.value}
      compact={compact}
      onChange={val => {
        onChange(localesOptions.find(option => option.value === val) as LocaleOption)
      }}
      options={localesOptions}
      data-testid="locale-select"
      aria-label="locale-select"
      isLocaleSelector={usePreLoginStyles}
      defaultValue={defaultLocale}
      allowDeselect={false}
    />
  )
}
